import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import useGqlClient from "@/src/hooks/useGqlClient";
import { useInvalidateQueryKeys } from "@/src/hooks/useInvalidateQueryKeys";
import {
  DataRoomFolderBrowseQuery,
  DataRoomFolderQuery,
  useMoveDataRoomFileMutation,
  useMoveDataRoomFolderMutation,
} from "@/src/graphql/generated";
import { toasts } from "@/src/components/toasts/toasts";

type FolderListQuery = DataRoomFolderQuery | DataRoomFolderBrowseQuery;

type MoveItemsArgs = {
  sourceFolderId: string;
  targetFolderId: string;
  targetFolderName: string;
  fileIds: string[];
  folderIds: string[];
  onMoved?: () => void;
};

type RemoveItemsArgs = {
  sourceFolderId: string;
  fileIds: string[];
  folderIds: string[];
};

function removeItemsFromDataRoomFolderQuery(
  old: DataRoomFolderQuery | undefined,
  args: RemoveItemsArgs,
): DataRoomFolderQuery | undefined {
  if (!old?.dataRoomFolder) {
    return old;
  }
  if (old.dataRoomFolder.id !== args.sourceFolderId) {
    return old;
  }

  return {
    ...old,
    dataRoomFolder: {
      ...old.dataRoomFolder,
      files: old.dataRoomFolder.files.filter(
        (file) => !args.fileIds.includes(file.id),
      ),
      folders: old.dataRoomFolder.folders.filter(
        (folder) => !args.folderIds.includes(folder.id),
      ),
    },
  };
}

function removeItemsFromDataRoomFolderBrowseQuery(
  old: DataRoomFolderBrowseQuery | undefined,
  args: RemoveItemsArgs,
): DataRoomFolderBrowseQuery | undefined {
  if (!old?.dataRoomFolder) {
    return old;
  }
  if (old.dataRoomFolder.id !== args.sourceFolderId) {
    return old;
  }

  return {
    ...old,
    dataRoomFolder: {
      ...old.dataRoomFolder,
      files: old.dataRoomFolder.files.filter(
        (file) => !args.fileIds.includes(file.id),
      ),
      folders: old.dataRoomFolder.folders.filter(
        (folder) => !args.folderIds.includes(folder.id),
      ),
    },
  };
}

function formatMovedMessage(
  fileCount: number,
  folderCount: number,
  targetFolderName: string,
) {
  const total = fileCount + folderCount;
  if (total === 1) {
    if (fileCount === 1) {
      return `Moved file to ${targetFolderName}`;
    }
    return `Moved folder to ${targetFolderName}`;
  }
  return `Moved ${total} items to ${targetFolderName}`;
}

export function useMoveItemsWithUndo() {
  const client = useGqlClient();
  const queryClient = useQueryClient();
  const {
    invalidateDataRoomFolders,
    invalidateDealActivities,
    invalidateDataRooms,
  } = useInvalidateQueryKeys();

  const moveDataRoomFile = useMoveDataRoomFileMutation(client);
  const moveDataRoomFolder = useMoveDataRoomFolderMutation(client);

  const optimisticallyRemoveFromSource = useCallback(
    (args: {
      sourceFolderId: string;
      fileIds: string[];
      folderIds: string[];
    }) => {
      const previousQueries = [
        ...queryClient.getQueriesData<DataRoomFolderQuery>({
          queryKey: ["DataRoomFolder"],
        }),
        ...queryClient.getQueriesData<DataRoomFolderBrowseQuery>({
          queryKey: ["DataRoomFolderBrowse"],
        }),
      ];

      queryClient.setQueriesData<DataRoomFolderQuery>(
        { queryKey: ["DataRoomFolder"] },
        (old) => removeItemsFromDataRoomFolderQuery(old, args),
      );
      queryClient.setQueriesData<DataRoomFolderBrowseQuery>(
        { queryKey: ["DataRoomFolderBrowse"] },
        (old) => removeItemsFromDataRoomFolderBrowseQuery(old, args),
      );

      return previousQueries;
    },
    [queryClient],
  );

  const restoreQueries = useCallback(
    (
      previousQueries: ReturnType<
        typeof queryClient.getQueriesData<FolderListQuery>
      >,
    ) => {
      for (const [queryKey, data] of previousQueries) {
        queryClient.setQueryData(queryKey, data);
      }
    },
    [queryClient],
  );

  const runMoves = useCallback(
    async (args: {
      targetFolderId: string;
      fileIds: string[];
      folderIds: string[];
    }) => {
      await Promise.all([
        ...args.fileIds.map((id) =>
          moveDataRoomFile.mutateAsync({
            input: {
              id,
              folderID: args.targetFolderId,
            },
          }),
        ),
        ...args.folderIds.map((id) =>
          moveDataRoomFolder.mutateAsync({
            input: {
              folderID: id,
              targetParentFolderID: args.targetFolderId,
            },
          }),
        ),
      ]);
    },
    [moveDataRoomFile, moveDataRoomFolder],
  );

  const moveItems = useCallback(
    async (args: MoveItemsArgs) => {
      const {
        sourceFolderId,
        targetFolderId,
        targetFolderName,
        fileIds,
        folderIds,
        onMoved,
      } = args;

      if (fileIds.length === 0 && folderIds.length === 0) {
        return;
      }

      if (folderIds.includes(targetFolderId)) {
        toasts.error("Cannot move a folder into itself");
        return;
      }

      const previousQueries = optimisticallyRemoveFromSource({
        sourceFolderId,
        fileIds,
        folderIds,
      });

      try {
        await runMoves({ targetFolderId, fileIds, folderIds });

        invalidateDataRoomFolders();
        invalidateDealActivities();
        invalidateDataRooms();
        onMoved?.();

        toasts.withAction(
          formatMovedMessage(fileIds.length, folderIds.length, targetFolderName),
          {
            label: "Undo",
            onClick: () => {
              void (async () => {
                try {
                  await runMoves({
                    targetFolderId: sourceFolderId,
                    fileIds,
                    folderIds,
                  });
                  invalidateDataRoomFolders();
                  invalidateDealActivities();
                  invalidateDataRooms();
                  toasts.info("Move undone");
                } catch {
                  toasts.error("Failed to undo move");
                  invalidateDataRoomFolders();
                }
              })();
            },
          },
        );
      } catch {
        restoreQueries(previousQueries);
        toasts.error("Failed to move items");
      }
    },
    [
      optimisticallyRemoveFromSource,
      restoreQueries,
      runMoves,
      invalidateDealActivities,
      invalidateDataRooms,
      invalidateDataRoomFolders,
    ],
  );

  return { moveItems };
}
